/**
 * Central helper for inspecting browser storage availability before writing
 * to IndexedDB. Backed by the Storage API (`navigator.storage.estimate`).
 *
 * All functions degrade gracefully on browsers that do not expose the API:
 *  - `getStorageEstimate` resolves to `null`
 *  - `hasIndexedDbSpace` returns `true` (i.e. allow the write attempt; let the
 *    underlying IDB request surface a QuotaExceededError if it actually fails)
 */

// Keep a small safety buffer so we never fill the quota to the brim — leaves
// room for transient writes from other stores (scsFileCache, etc.).
const SAFETY_MARGIN_BYTES = 5 * 1024 * 1024; // 5 MB

export interface StorageEstimateResult {
  quota: number;
  usage: number;
  available: number;
}

export async function getStorageEstimate(): Promise<StorageEstimateResult | null> {
  try {
    if (typeof navigator === "undefined" || !navigator.storage?.estimate) {
      return null;
    }
    const { quota = 0, usage = 0 } = await navigator.storage.estimate();
    return {
      quota,
      usage,
      available: Math.max(0, quota - usage),
    };
  } catch (error) {
    console.warn("Failed to read storage estimate:", error);
    return null;
  }
}

/**
 * Returns `true` when there is room in the persistent storage for `requiredBytes`
 * (after applying a small safety margin). When the Storage API is unavailable
 * we optimistically return `true` so the caller can still attempt the write.
 *
 * @param requiredBytes Bytes the caller intends to write. Defaults to 0, which
 *                      just checks that *any* room above the safety margin exists.
 */
export async function hasIndexedDbSpace(requiredBytes = 0): Promise<boolean> {
  const estimate = await getStorageEstimate();
  if (!estimate) return true;
  return estimate.available >= requiredBytes + SAFETY_MARGIN_BYTES;
}

/**
 * Best-effort byte-size estimate for a JSON-serializable payload. Uses Blob to
 * get an accurate UTF-8 byte count without holding a second copy in memory for
 * long. Returns 0 when serialization fails (e.g. circular refs) — pair with
 * `hasIndexedDbSpace()` to still gate on remaining quota.
 */
export function estimatePayloadBytes(data: unknown): number {
  if (data == null) return 0;
  if (data instanceof ArrayBuffer) return data.byteLength;
  if (ArrayBuffer.isView(data)) return data.byteLength;
  if (data instanceof Blob) return data.size;
  try {
    return new Blob([JSON.stringify(data)]).size;
  } catch {
    return 0;
  }
}
